/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import axiosInstance from '../functions/AxiosInstance'; // Asegúrate de que la ruta sea correcta




// Función para crear un template usando FormData de manera menos verbosa
const createTemplate = async (templateData: {
  title: string;
  description: string;
  imageUrls?: string[];
  enterpriseGradeSolution: boolean;
  premiumLicense: boolean;
  price: number;
  paymentModel: string;
  lifetimeUpdates: boolean;
  keyFeatures: string[];
  includedPages: string[];
  sslSecuredTransaction: boolean;
  moneyBackGuarantee: boolean;
  zipFile: File | null;
  technologyUsed: string[];
  rating?: { average: number; totalRatings: number; users: Array<{ userId: string; score: number }> };
  likes?: { count: number; users: Array<string> };
  dislikes?: { count: number; users: Array<string> };
}) => {
  const formData = new FormData();

  // Iterar sobre las claves del objeto y agregar las propiedades al FormData
  Object.entries(templateData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value)); // Si es un array, convertir a JSON
    } else if (value instanceof File) {
      formData.append(key, value); // Si es un archivo, agregarlo directamente
    } else if (value !== undefined && value !== null) {
      formData.append(key, value.toString()); // Para valores simples, agregar como string
    }
  });

  // Enviar la solicitud POST con FormData
  const response = await axiosInstance.post('/templates', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data; // Devuelve los datos de la respuesta
};

// Hook para crear un template
export const useSubmitTemplate = () => {
  const mutation = useMutation({
    mutationFn: createTemplate, // Función para crear el template
    onSuccess: () => {
      toast.success('Template creado con éxito.');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Error desconocido';
      toast.error(`Error al crear el template: ${errorMessage}`);
      console.error('Error al crear el template:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la creación fue exitosa
  };
};



// Función para obtener los templates con paginación
const fetchTemplates = async (page = 1, limit = 10) => {
  const response = await axiosInstance.get(`/templates?page=${page}&limit=${limit}`);
  return response.data;
};


interface Template {
  id: string;
  title: string;
  category: string;
  marketplace: string;
  price: number;
}

interface FetchTemplatesResponse {
  templates: Template[];
  totalTemplates: number;
  totalPages: number;
  currentPage: number;
}

export const useGetTemplates = (page: number = 1, limit: number = 10) => {
  const { data, error, isLoading, isError } = useQuery<FetchTemplatesResponse, Error>({
    queryKey: ['templates', page, limit],
    queryFn: async (): Promise<FetchTemplatesResponse> => {
      try {
        return await fetchTemplates(page, limit);
      } catch (err: any) {
        const errorMessage = err?.response?.data?.message || err?.message || 'Error desconocido';
        console.error('Error al obtener los templates:', err);
        throw new Error(errorMessage);
      }
    },
    refetchOnWindowFocus: false,
  });

  return {
    templates: data?.templates || [],
    totalTemplates: data?.totalTemplates || 0,
    totalPages: data?.totalPages || 1,
    currentPage: data?.currentPage || 1,
    isLoading,
    isError,
    error,
  };
};




// Función para obtener un template por su ID
const fetchTemplateById = async (templateId: string) => {
    const response = await axiosInstance.get(`/templates/${templateId}`);
    return response.data.templates; // Devuelve los datos de la respuesta
  };
  
  export const useGetTemplateById = (templateId: string) => {
    const { data, error, isLoading, isError } = useQuery({
      queryKey: ['template', templateId], // Identificador único para la consulta (utilizando el templateId)
      queryFn: async () => {
        try {
          return await fetchTemplateById(templateId); // Llama a la función para obtener el template por ID
        } catch (error: any) {
          console.error('Error al obtener el template:', error);
          throw error;
        }
      },
      refetchOnWindowFocus: false, // Evita que los datos se vuelvan a obtener cuando el foco de la ventana cambia
    });
  
    return {
      template: data,    // El template obtenido
      isLoading,         // Estado de carga
      isError,           // Si hubo un error
      error,             // El error completo (si existe)
    };
  };
  



  // Función para eliminar un template por su ID
const deleteTemplateById = async (templateId: string) => {
    const response = await axiosInstance.delete(`/templates/${templateId}`);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  export const useDeleteTemplate = () => {
    const mutation = useMutation({
      mutationFn: deleteTemplateById, // Función para eliminar el template por ID
      onSuccess: () => {
        toast.success('Template eliminado con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Error desconocido';
        toast.error(`Error al eliminar el template: ${errorMessage}`);
        console.error('Error al eliminar el template:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la eliminación fue exitosa
    };
  };
  


  // Función para enviar una reacción (como 'like' o 'dislike') a un template
const giveReaction = async (reactionData: { templateId: string; reaction: 'like' | 'dislike' }) => {
  const response = await axiosInstance.post('/templates/templates/users/reaction', reactionData);
  return response.data; // Devuelve los datos de la respuesta
};

// Hook para manejar la reacción
export const useGiveReaction = () => {
  const mutation = useMutation({
    mutationFn: giveReaction, // Función para enviar la reacción
    onSuccess: () => {
      toast.success('Reacción enviada con éxito.');
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Error desconocido';

      toast.error(errorMessage); // Mostrar solo el mensaje del backend
      console.error('Error al enviar la reacción:', errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la reacción fue enviada con éxito
  };
};

  

  // Función para enviar una calificación a un template
const giveRating = async (ratingData: { templateId: string;  rating: number }) => {
    const response = await axiosInstance.post('/templates/rating', ratingData);
    return response.data; // Devuelve los datos de la respuesta
  };
  
  // Hook para manejar la calificación
  export const useGiveRating = () => {
    const mutation = useMutation({
      mutationFn: giveRating, // Función para enviar la calificación
      onSuccess: () => {
        toast.success('Calificación enviada con éxito.');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Error desconocido';
        toast.error(`Error al enviar la calificación: ${errorMessage}`);
        console.error('Error al enviar la calificación:', errorMessage);
      },
    });
  
    return {
      ...mutation,
      isLoading: mutation.isPending, // Estado de carga de la mutación
      isSuccess: mutation.isSuccess, // Indica si la calificación fue enviada con éxito
    };
  };
  



// Función para registrar una vista de plantilla
const registerView = async ({ templateId }: { templateId: string }) => {
  try {
    const response = await axiosInstance.post(`templates/register-view/${templateId}`);
    return response.data; // Devuelve los datos de la respuesta
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error desconocido';
    toast.error(`Error al registrar la vista de la plantilla: ${errorMessage}`);
    console.error('Error al registrar la vista de la plantilla:', errorMessage);
    throw new Error(errorMessage); // Lanza el error para manejarlo en el hook
  }
};

// Hook para registrar la vista de una plantilla
export const useRegisterView = () => {
  const mutation = useMutation({
    mutationFn: registerView,
    onSuccess: () => {
      toast.success('Vista registrada con éxito.');
    },
    onError: (error: any) => {
      // Captura el mensaje de error exacto enviado por el servidor
      const errorMessage = error?.message || 'Error desconocido';
      console.error('Error al registrar la vista de la plantilla:', errorMessage);
      toast.error(errorMessage);
    },
  });

  return {
    ...mutation,
    isLoading: mutation.isPending, // Estado de carga de la mutación
    isSuccess: mutation.isSuccess, // Indica si la operación fue exitosa
    error: mutation.error?.message || '', // Captura el error correctamente
  };
};
